import React from 'react';

/**
 * ButtonGroup Component
 * 
 * Groups buttons together in a segment control style
 * Pattern: iOS Segmented Control, GitHub Action Bar, Linear Toolbar
 * 
 * Features:
 * - Horizontal or vertical orientation
 * - Full-width option
 * - Seamless button joining
 * - Touch-friendly on mobile
 * - Keyboard navigation support
 * 
 * Use Cases:
 * - Execution mode toggle (parallel/sequential)
 * - Time range filters (today/week/month)
 * - View switches (table/grid/list)
 * - Status filters (all/passed/failed)
 * 
 * @example
 * ```tsx
 * <ButtonGroup>
 *   <Button variant="outline" size="sm">Today</Button>
 *   <Button variant="outline" size="sm">Week</Button>
 *   <Button variant="outline" size="sm">Month</Button>
 * </ButtonGroup>
 * 
 * <ButtonGroup orientation="vertical" fullWidth>
 *   <Button variant="secondary">Option 1</Button>
 *   <Button variant="secondary">Option 2</Button>
 * </ButtonGroup>
 * ```
 */

export interface ButtonGroupProps {
  /** Button components to group */
  children: React.ReactNode;
  
  /** Layout orientation */
  orientation?: 'horizontal' | 'vertical';
  
  /** Take full width of container */
  fullWidth?: boolean;
  
  /** Additional CSS classes */
  className?: string;
  
  /** Spacing between buttons (only for non-attached mode) */
  spacing?: 'none' | 'sm' | 'md';
  
  /** Whether buttons should be attached (no gap) */
  attached?: boolean;
}

/**
 * Spacing class mapping
 */
const spacingStyles = {
  none: '',
  sm: 'gap-1',
  md: 'gap-2'
};

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  children,
  orientation = 'horizontal',
  fullWidth = false,
  className = '',
  spacing = 'none',
  attached = true
}) => {
  // Base classes
  const baseClasses = `
    inline-flex
    ${orientation === 'horizontal' ? 'flex-row' : 'flex-col'}
    ${fullWidth ? 'w-full' : ''}
  `.trim().replace(/\s+/g, ' ');

  // Attached mode: Seamless joining
  const attachedClasses = attached ? `
    ${orientation === 'horizontal' ? '[&>*:not(:first-child)]:rounded-l-none [&>*:not(:first-child)]:-ml-[2px]' : ''}
    ${orientation === 'horizontal' ? '[&>*:not(:last-child)]:rounded-r-none' : ''}
    ${orientation === 'vertical' ? '[&>*:not(:first-child)]:rounded-t-none [&>*:not(:first-child)]:-mt-[2px]' : ''}
    ${orientation === 'vertical' ? '[&>*:not(:last-child)]:rounded-b-none' : ''}
    [&>*]:flex-1
  `.trim().replace(/\s+/g, ' ') : '';

  // Spacing mode: Gaps between buttons
  const spacingClass = !attached ? spacingStyles[spacing] : '';

  // Full width: Each button takes equal space
  const fullWidthChildClass = fullWidth && !attached ? '[&>*]:flex-1' : '';

  const combinedClassName = [
    baseClasses,
    attachedClasses,
    spacingClass,
    fullWidthChildClass,
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={combinedClassName} role="group">
      {children}
    </div>
  );
};

/**
 * Convenience components for common patterns
 */

/** Horizontal segmented control (most common) */
export const SegmentedControl: React.FC<Omit<ButtonGroupProps, 'orientation' | 'attached'>> = (props) => (
  <ButtonGroup orientation="horizontal" attached={true} {...props} />
);

/** Vertical button stack */
export const VerticalButtonStack: React.FC<Omit<ButtonGroupProps, 'orientation'>> = (props) => (
  <ButtonGroup orientation="vertical" {...props} />
);

/** Toolbar (buttons with spacing) */
export const Toolbar: React.FC<Omit<ButtonGroupProps, 'attached' | 'spacing'>> = (props) => (
  <ButtonGroup attached={false} spacing="sm" {...props} />
);

